import { useState, type ReactNode } from 'react';
import { Button, Icon, TierBadge, type IconName } from '@garnet/ui';
import type {
  AdvConfidence,
  AdvCtx,
  Cite,
  Quarantine,
  RecAccepted,
  RecModified,
  RecPending,
  RecRejected,
  TpmTier,
} from '../data/types.js';

/**
 * AI Advisor shared components — faithful port of advisor-components.jsx. Reuses the
 * console primitives and adds the Advisor's own quiet-iris identity: the mark, the citation
 * chip (first-class grounding), the confidence label, and the recommendation card with its
 * four human-response states. None of these ever shows an assurance-state colour.
 */

/** Advisor mark — a hollow diamond, kin to the gem but never filled. */
export function AdvisorMark({ size = 15 }: { size?: number }) {
  return (
    <span className="adv-mark" aria-hidden="true">
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round">
        <rect x="6.7" y="6.7" width="10.6" height="10.6" rx="1.4" transform="rotate(45 12 12)" />
        <circle cx="12" cy="12" r="1.5" fill="currentColor" stroke="none" />
      </svg>
    </span>
  );
}

export function AdvisorEyebrow({ verb = 'recommends', of }: { verb?: string; of?: ReactNode }) {
  return (
    <span className="adv-eyebrow">
      <AdvisorMark size={13} />
      Advisor
      <span className="of">· {verb}</span>
      {of && <span className="of">· {of}</span>}
    </span>
  );
}

const CITE_GLYPH: Record<Cite['kind'], IconName> = {
  rule: 'filter',
  check: 'check',
  source: 'refresh',
  policy: 'scan',
  doc: 'file',
};

export function CitationChip({ c }: { c: Cite }) {
  const glyph = CITE_GLYPH[c.kind] ?? 'dot';
  const title = (c.untrusted ? 'untrusted source · ' : 'grounded in ') + c.kind + ' · ' + c.k + (c.s ? ' · ' + c.s : '');
  return (
    <span className={'cite' + (c.untrusted ? ' untrusted' : '')} role="link" tabIndex={0} title={title}>
      <Icon name={glyph} size={12} style={{ width: 12, height: 12 }} />
      <span className="ck">{c.k}</span>
      {c.s && <span className="cs">· {c.s}</span>}
    </span>
  );
}

export function Citations({ cites, label = 'grounding' }: { cites?: Cite[]; label?: string }) {
  if (!cites || cites.length === 0) return null;
  return (
    <div className="cite-row">
      <span className="cite-lab">{label}</span>
      {cites.map((c, i) => (
        <CitationChip key={i} c={c} />
      ))}
    </div>
  );
}

/** Confidence — quiet mono, never a colour. */
export function Confidence({ level }: { level: AdvConfidence }) {
  return (
    <span className={'conf ' + level} title={'Advisor confidence · ' + level}>
      <span className="cl">confidence</span>
      <span className="bars">
        <i />
        <i />
        <i />
      </span>
      <span className="cv">{level}</span>
    </span>
  );
}

export function ProvenanceNote({ children }: { children: ReactNode }) {
  return (
    <div className="prov-note" role="note">
      <span className="pi">
        <Icon name="alert" size={13} />
      </span>
      <span>{children}</span>
    </div>
  );
}

/**
 * The inline recommendation card. States: pending (default · three actions) /
 * accepted / rejected. It proposes a human action; the human accepts, modifies, or rejects,
 * and the response is recorded against their name (the advice-only boundary made visible).
 */
export function RecommendationCard({
  rec,
  acceptLink,
  onAccept,
  onModify,
  onReject,
}: {
  rec: RecPending;
  acceptLink?: string;
  onAccept?: (rec: RecPending) => void;
  onModify?: (rec: RecPending) => void;
  onReject?: (rec: RecPending, reason: string) => void;
}) {
  const [state, setState] = useState<'pending' | 'accepted' | 'rejected'>('pending');
  const [reason, setReason] = useState('');
  const [showReason, setShowReason] = useState(false);

  if (state === 'accepted') {
    return (
      <div className="adv-card resolved">
        <div className="adv-resolved-bar">
          <span className="rb-tag accepted">accepted</span>
          <span className="rb-body">
            <b>{rec.title}</b>
            <br />
            <span className="rb-who">You · just now — performed in one step.</span>{' '}
            <span className="rb-link">{acceptLink ?? 'request raised'}</span>
          </span>
        </div>
      </div>
    );
  }
  if (state === 'rejected') {
    return (
      <div className="adv-card resolved">
        <div className="adv-resolved-bar">
          <span className="rb-tag rejected">rejected</span>
          <span className="rb-body">
            <b className="rb-strike">{rec.title}</b>
            <br />
            <span className="rb-who">You · just now{reason ? ' — “' + reason + '”' : ' — dismissed without a reason.'}</span>
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="adv-card">
      <div className="ac-head">
        <div style={{ minWidth: 0 }}>
          <AdvisorEyebrow of={rec.ctx ? rec.ctx.name : null} />
          <h3 className="ac-title" style={{ marginTop: 8 }}>
            {rec.title}
          </h3>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 7, flex: 'none' }}>
          <span className="adv-state pending">pending</span>
          <Confidence level={rec.confidence} />
        </div>
      </div>

      {rec.rationale && <p className="ac-rationale">{rec.rationale}</p>}

      <div style={{ marginTop: 11 }}>
        <Citations cites={rec.cites} />
      </div>

      {rec.provNote && <ProvenanceNote>{rec.provNote}</ProvenanceNote>}

      {rec.proposed && (
        <div className="ac-proposed">
          <span className="pl">you act</span>
          <span className="pt">{rec.proposed}</span>
        </div>
      )}

      {!showReason ? (
        <div className="ac-actions">
          <Button
            variant="primary"
            sm
            icon="check"
            onClick={() => {
              setState('accepted');
              onAccept?.(rec);
            }}
          >
            {rec.perform || 'Accept'}
          </Button>
          <Button variant="secondary" sm icon="exchange" onClick={() => onModify?.(rec)}>
            Modify
          </Button>
          <Button variant="ghost" sm icon="x" onClick={() => setShowReason(true)}>
            Reject
          </Button>
        </div>
      ) : (
        <div style={{ marginTop: 12, display: 'grid', gap: 9 }}>
          <input
            className="input"
            placeholder="Reason for rejecting (optional)"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            style={{ fontSize: 13 }}
          />
          <div className="ac-actions" style={{ marginTop: 0 }}>
            <Button
              variant="primary"
              sm
              icon="x"
              onClick={() => {
                setState('rejected');
                onReject?.(rec, reason);
              }}
            >
              Confirm reject
            </Button>
            <Button
              variant="ghost"
              sm
              onClick={() => {
                setShowReason(false);
                setReason('');
              }}
            >
              Cancel
            </Button>
          </div>
        </div>
      )}

      {!showReason && (
        <div className="ac-foot">
          The advisor proposes. <b>You</b> accept, modify, or reject — the action is recorded against your name.
        </div>
      )}
    </div>
  );
}

/** Pre-resolved card variants for the gallery (static demonstrations of the human response). */
export function ResolvedAccepted({ rec }: { rec: RecAccepted }) {
  return (
    <div className="adv-card resolved">
      <div className="adv-resolved-bar">
        <span className="rb-tag accepted">accepted</span>
        <span className="rb-body">
          <b>{rec.title}</b>
          <br />
          <span className="rb-who">
            {rec.by} · {rec.at} — accepted as proposed.
          </span>{' '}
          <span className="rb-link">{rec.result} →</span>
        </span>
      </div>
    </div>
  );
}

export function ResolvedModified({ rec }: { rec: RecModified }) {
  return (
    <div className="adv-card resolved">
      <div className="adv-resolved-bar">
        <span className="rb-tag modified">modified</span>
        <span className="rb-body" style={{ width: '100%' }}>
          <b>{rec.title}</b>
          <div style={{ marginTop: 9, display: 'grid', gap: 6 }}>
            <div style={{ display: 'flex', gap: 9, alignItems: 'baseline' }}>
              <span style={{ fontFamily: 'var(--mono)', fontSize: 9.5, fontWeight: 600, letterSpacing: '.06em', textTransform: 'uppercase', color: 'var(--muted)', width: 64, flex: 'none' }}>
                proposed
              </span>
              <span className="rb-strike" style={{ fontSize: 12.5 }}>
                {rec.proposedText}
              </span>
            </div>
            <div style={{ display: 'flex', gap: 9, alignItems: 'baseline' }}>
              <span style={{ fontFamily: 'var(--mono)', fontSize: 9.5, fontWeight: 600, letterSpacing: '.06em', textTransform: 'uppercase', color: 'var(--attest-ink)', width: 64, flex: 'none' }}>
                raised
              </span>
              <span style={{ fontSize: 12.5, color: 'var(--ink)' }}>{rec.modifiedText}</span>
            </div>
          </div>
          <div style={{ marginTop: 9 }}>
            <span className="rb-who">
              {rec.by} · {rec.at} — {rec.note}
            </span>{' '}
            <span className="rb-link">{rec.link} →</span>
          </div>
        </span>
      </div>
    </div>
  );
}

export function ResolvedRejected({ rec }: { rec: RecRejected }) {
  return (
    <div className="adv-card resolved">
      <div className="adv-resolved-bar">
        <span className="rb-tag rejected">rejected</span>
        <span className="rb-body">
          <b className="rb-strike">{rec.title}</b>
          <br />
          <span className="rb-who">
            {rec.by} · {rec.at}
          </span>
          <div style={{ fontSize: 12.5, color: 'var(--ink)', marginTop: 6, lineHeight: 1.5 }}>“{rec.reason}”</div>
        </span>
      </div>
    </div>
  );
}

/** Quarantine notice — a document that tried to instruct the model, isolated before grounding. */
export function QuarantineNote({ q }: { q: Quarantine }) {
  return (
    <div className="quarantine" role="note">
      <div className="qh">
        <span style={{ color: 'var(--flag)' }}>
          <Icon name="alert" size={16} />
        </span>
        <span className="qt">Quarantined · prompt-injection attempt</span>
        <span className="qd">{q.detected}</span>
      </div>
      <div className="qbody">{q.body}</div>
      <div className="qsnip">
        <span className="strip">isolated</span>
        {q.snippet}
      </div>
      <div className="qfoot">
        <Icon name="file" size={12} /> {q.doc} · flagged on the record · excluded from grounding
      </div>
    </div>
  );
}

export function ContextStrip({ ctx, tier }: { ctx: AdvCtx; tier?: TpmTier }) {
  return (
    <div className="ctx">
      <span className="cx-k">{ctx.kind}</span>
      <span style={{ fontFamily: 'var(--mono)', fontSize: 11.5, color: 'var(--muted)' }}>{ctx.id}</span>
      <span className="cx-name">{ctx.name}</span>
      <span className="cx-meta">{ctx.meta}</span>
      <span className="cx-spacer" />
      {tier && <TierBadge level={tier} sm />}
    </div>
  );
}
